import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import LoadingSpinner from './LoadingSpinner';
import { useAuth } from '../contexts/AuthContext';
import { normalizeMobileNumber } from '../utils/mobileValidation';
import { getApiUrl } from '../utils/api';
import PhoneCountrySelector from './phone/PhoneCountrySelector';
import { getPhoneCountryByIso2 } from '../constants/phoneCountries';

const AUTH_ERROR_MESSAGES: Record<string, string> = {
  invalid_mobile_number: 'صيغة رقم الجوال غير صحيحة',
  invalid_mobile_format: 'صيغة رقم الجوال غير صحيحة',
  mobile_number_required: 'رقم الجوال مطلوب',
  mobile_already_registered: 'هذا الرقم مستخدم بالفعل',
};

const parseBooleanFlag = (value: string | undefined, defaultValue: boolean): boolean => {
  if (value === undefined) {
    return defaultValue;
  }

  const normalized = value.trim().toLowerCase();
  if (['false', '0', 'off', 'no'].includes(normalized)) {
    return false;
  }
  if (['true', '1', 'on', 'yes'].includes(normalized)) {
    return true;
  }

  return defaultValue;
};

const PHONE_INTL_ENABLED = parseBooleanFlag(import.meta.env.VITE_PHONE_INTL_ENABLED, true);

const MobileBindingModal: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { pendingMobileBinding, completeMobileBinding, clearError } = useAuth();

  const [mobileInput, setMobileInput] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mobileCountry, setMobileCountry] = useState('SA');
  const effectiveMobileCountry = PHONE_INTL_ENABLED ? mobileCountry : 'SA';

  const phonePlaceholder = PHONE_INTL_ENABLED
    ? (getPhoneCountryByIso2(mobileCountry)?.example || '+9665XXXXXXXX')
    : '05XXXXXXXX';

  const mappedSubmitError = useMemo(() => {
    if (!submitError) {
      return null;
    }

    if (AUTH_ERROR_MESSAGES[submitError]) {
      return AUTH_ERROR_MESSAGES[submitError];
    }

    return submitError;
  }, [submitError]);

  const validateMobile = (value: string): string | null => {
    if (!value.trim()) {
      setFieldError('رقم الجوال مطلوب');
      return null;
    }

    const normalized = normalizeMobileNumber(value, effectiveMobileCountry);
    if (!normalized) {
      setFieldError(PHONE_INTL_ENABLED ? 'صيغة رقم الجوال غير صحيحة' : 'يرجى إدخال رقم جوال صحيح');
      return null;
    }

    setFieldError(null);
    return normalized;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const normalizedMobile = validateMobile(mobileInput);
    if (!normalizedMobile) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const response = await fetch(getApiUrl('AUTH_BIND_MOBILE'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('authToken') || ''}`,
        },
        body: JSON.stringify({ mobile_number: normalizedMobile, mobile_country: effectiveMobileCountry }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to bind mobile number');
      }

      completeMobileBinding(data.token, data.user);
      clearError();

      if (location.pathname === '/login' || location.pathname === '/signup') {
        const redirectTo = sessionStorage.getItem('postLoginRedirectTo') || location.state?.redirectTo || '/my-designs';
        sessionStorage.removeItem('postLoginRedirectTo');
        navigate(redirectTo, { replace: true });
      }
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Failed to bind mobile number');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!pendingMobileBinding) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[100]">
      <div className="absolute inset-0 bg-black/50" />
      <div className="relative flex min-h-screen items-center justify-center p-4">
        <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl border border-slate-200/60">
          <h2 className="text-2xl font-bold text-slate-800 text-center">
            ربط رقم الجوال
          </h2>
          <p className="mt-2 text-sm text-slate-600 text-center">
            {PHONE_INTL_ENABLED
              ? 'لاستمرار استخدام حسابك، يرجى ربط رقم جوال صحيح (محلي أو دولي).'
              : 'لاستمرار استخدام حسابك، يرجى ربط رقم جوال سعودي صحيح.'}
          </p>

          <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="binding-mobile" className="block text-sm font-semibold text-slate-700 mb-2">
                رقم الجوال
              </label>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(180px,220px)]">
                <PhoneCountrySelector
                  id="binding-mobile-country"
                  value={mobileCountry}
                  onChange={(iso2) => {
                    setMobileCountry(iso2);
                    if (fieldError) {
                      setFieldError(null);
                    }
                    if (submitError) {
                      setSubmitError(null);
                    }
                  }}
                  className={fieldError ? 'border-red-300 focus:ring-red-200 focus:border-red-400' : 'border-slate-200'}
                /><input
                  id="binding-mobile"
                  type="tel"
                  value={mobileInput}
                  onChange={(event) => {
                    setMobileInput(event.target.value);
                    if (fieldError) {
                      setFieldError(null);
                    }
                    if (submitError) {
                      setSubmitError(null);
                    }
                  }}
                  onBlur={() => {
                    validateMobile(mobileInput);
                  }}
                  className={`w-full px-4 py-4 border-2 rounded-xl bg-white text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#a57a6a]/20 focus:border-[#a57a6a] transition-all duration-200 text-left ${fieldError ? 'border-red-300 focus:ring-red-200 focus:border-red-400' : 'border-slate-200'
                    }`}
                  placeholder={phonePlaceholder}
                  dir="ltr"
                  autoComplete="tel"
                />

              </div>
              {fieldError && (
                <p className="mt-2 text-sm text-red-600 text-right">{fieldError}</p>
              )}
            </div>

            {mappedSubmitError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-right">
                <span className="text-sm font-medium">{mappedSubmitError}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-gradient-to-r from-[#a57a6a] to-[#a57a6a] hover:from-[#8a6a5a] hover:to-[#8a6a5a] disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold py-4 px-6 rounded-xl transition-all duration-300"
            >
              {isSubmitting ? (
                <span className="inline-flex items-center justify-center gap-2">
                  <LoadingSpinner size="sm" />
                  جارٍ ربط الرقم...
                </span>
              ) : (
                'ربط الرقم'
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default MobileBindingModal;
